import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";
import { submitLead } from "@/lib/api";
import { toast } from "sonner";

const ASSESSMENT_FEATURES = [
  {
    t: "Photo-Based Pre-Assessment",
    b: "Send us a few photos of your current setup — we'll review remotely first.",
  },
  {
    t: "Get A Same-Day Quote",
    b: "Send your details and photos of your current hot water setup, and our team will aim to provide a clear same-day quote where possible.",
  },
  {
    t: "Fast, Professional & Transparent Installs",
    b: "Clear communication, professional installation coordination and transparent pricing from assessment through to completion.",
  },
  {
    t: "Installing Across NZ",
    b: "Servicing households nationwide through our growing installer network.",
  },
];

export const LeadCaptureSection = () => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", suburb: "",
    current_system: "", household_size: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    // Browser-level required handles empties, but double-check explicitly:
    const required = ["name", "email", "phone", "suburb", "current_system", "household_size"];
    for (const k of required) {
      if (!form[k] || String(form[k]).trim() === "") {
        return toast.error("Please complete all fields to request your free assessment.");
      }
    }
    setSubmitting(true);
    try {
      await submitLead({
        type: "assessment",
        ...form,
        household_size: Number(form.household_size),
        notes: "Home page lead capture",
      });
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", suburb: "", current_system: "", household_size: "" });
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="assess" className="py-20 sm:py-28 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
        {/* Left column — copy + feature grid */}
        <Reveal className="lg:col-span-7 flex flex-col">
          <SectionLabel>Free assessment</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold text-[#0B132B] leading-[1.05]">
            Not sure which system suits your home?
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed max-w-xl">
            Start with a simple photo-based assessment and we&apos;ll guide you through the best upgrade pathway.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {ASSESSMENT_FEATURES.map((c, i) => (
              <div
                key={i}
                className="p-4 rounded-2xl bg-white border border-[#E2E8F0] h-full flex flex-col hover:border-[#10B981]/40 hover:shadow-md hover:shadow-emerald-500/5 transition-all"
              >
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#10B981]/10 text-[#10B981] shrink-0">
                    <BadgeCheck className="h-4 w-4" strokeWidth={2.2} />
                  </span>
                  <p className="font-heading font-semibold text-[#0B132B] text-sm leading-snug">{c.t}</p>
                </div>
                <p className="text-xs text-[#475569] leading-relaxed">{c.b}</p>
              </div>
            ))}
          </div>

          <Link
            to="/savings-calculator"
            data-testid="assess-cta"
            className="mt-8 self-start inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/25"
          >
            Calculate Your Potential Savings <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>

        {/* Right column — form, aligned to top of heading */}
        <Reveal delay={120} className="lg:col-span-5 w-full">
          <div className="lg:sticky lg:top-28">
            <Card
              data-testid="home-lead-form"
              className="rounded-3xl p-7 sm:p-8 border-[#E2E8F0] bg-white shadow-xl shadow-emerald-500/5"
            >
              <div className="mb-5">
                <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#10B981] font-semibold">Request Your Assessment</p>
                <p className="font-heading mt-1 text-xl font-bold text-[#0B132B] leading-tight">
                  All fields are required so we can quote accurately.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4" noValidate={false}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="home-name" className="text-xs">Name *</Label>
                    <Input
                      id="home-name"
                      data-testid="home-form-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <Label htmlFor="home-email" className="text-xs">Email *</Label>
                    <Input
                      id="home-email"
                      data-testid="home-form-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <Label htmlFor="home-phone" className="text-xs">Phone *</Label>
                    <Input
                      id="home-phone"
                      data-testid="home-form-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div>
                    <Label htmlFor="home-suburb" className="text-xs">Suburb / Region *</Label>
                    <Input
                      id="home-suburb"
                      data-testid="home-form-suburb"
                      value={form.suburb}
                      onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="home-system" className="text-xs">Current hot water system *</Label>
                    <select
                      id="home-system"
                      data-testid="home-form-system"
                      value={form.current_system}
                      onChange={(e) => setForm({ ...form, current_system: e.target.value })}
                      required
                      aria-required="true"
                      className="w-full border border-[#E2E8F0] rounded-md px-3 h-10 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981]"
                    >
                      <option value="">Select…</option>
                      <option value="continuous_gas">Continuous gas</option>
                      <option value="gas_storage">Gas storage</option>
                      <option value="electric_storage">Electric storage cylinder</option>
                      <option value="lpg">LPG bottle</option>
                      <option value="not_sure">Not sure</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="home-people" className="text-xs">People in home *</Label>
                    <Input
                      id="home-people"
                      data-testid="home-form-people"
                      type="number"
                      min="1"
                      max="20"
                      value={form.household_size}
                      onChange={(e) => setForm({ ...form, household_size: e.target.value })}
                      required
                      aria-required="true"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="home-form-submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white py-6 font-semibold text-base"
                >
                  {submitting ? "Sending..." : "Request Free Assessment"}
                </Button>
                <p className="text-[10px] text-[#475569] text-center">
                  Estimate only. Final pricing depends on site assessment.
                </p>
              </form>
            </Card>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
